import { Image } from 'expo-image';
import * as React from 'react';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Card, Text } from '@/ui';

interface ModelCardItemProps {
  imageSrc: string;
  title: string;
  subtitle: string;
  cost: string;
  unit: string;
  style?: any;
  disabled?: boolean;
  active?: boolean;
  hideInitiationFee?: boolean;
  onPress?: () => void;
}

export const ModelCardItem = ({
  imageSrc,
  title,
  subtitle,
  cost,
  unit,
  style,
  disabled = false,
  active = false,
  hideInitiationFee = false,
  onPress,
}: ModelCardItemProps) => {
  const { styles, theme } = useStyles(stylesheet);
  const [hover, setHover] = React.useState(false);

  const getStyle = () => {
    const showDisabled = disabled;
    let stateStyle = styles.default;

    if (hover) {
      stateStyle = styles.hover;
    }
    if (active) {
      stateStyle = styles.active;
    }
    if (showDisabled) {
      stateStyle = styles.disabled;
    }

    return [stateStyle];
  };

  return (
    <Pressable
      style={[styles.container, style]}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      hitSlop={8}
      onPress={() => {
        onPress?.();
      }}
      disabled={disabled}
    >
      <Card style={[styles.card, getStyle()]}>
        <View style={styles.cardContentContainer}>
          <Image source={imageSrc} contentFit="contain" style={styles.image} />

          <View style={{ flexShrink: 1 }}>
            <Text type="bodyBold">{title}</Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {subtitle}
            </Text>
          </View>
        </View>

        <View style={styles.cardPricingContainer}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Text type="bodyBold">{cost}</Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              /
            </Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {unit}
            </Text>
          </View>

          {!hideInitiationFee ? (
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              + $99 Initiation Fee
            </Text>
          ) : null}
        </View>
      </Card>
    </Pressable>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    width: '100%',
  },
  card: {
    flex: 1,
    flexDirection: {
      xs: 'row',
      md: 'column',
    },
    justifyContent: 'space-between',
    alignItems: {
      xs: 'center',
      md: 'flex-start',
    },
    padding: 16,
    gap: 16,
  },
  cardContentContainer: {
    flexDirection: {
      xs: 'row',
      md: 'column',
    },
    gap: 8,
    alignItems: {
      xs: 'center',
      md: 'flex-start',
    },
    width: {
      xs: '60%',
      md: '100%',
    },
  },
  image: {
    width: {
      xs: 70,
      md: '100%',
    },
    height: {
      xs: 55,
      md: 120,
    },
  },
  cardPricingContainer: {
    flexDirection: 'column',
    alignItems: {
      xs: 'flex-end',
      md: 'flex-start',
    },
    gap: 4,
  },
  default: {
    backgroundColor: theme.colors.card.default.backgroundColor,
    borderColor: theme.colors.card.default.borderColor,
  },
  active: {
    backgroundColor: theme.colors.card.active.backgroundColor,
    borderColor: theme.colors.card.active.borderColor,
    borderWidth: 1,
  },
  hover: {
    backgroundColor: theme.colors.card.hover.backgroundColor,
    borderColor: theme.colors.card.hover.borderColor,
  },
  disabled: {
    backgroundColor: theme.colors.card.disabled.backgroundColor,
    borderColor: theme.colors.card.disabled.borderColor,
    opacity: 0.4,
  },
}));
